<template>
  <div class="max-width">
    <v-row v-if="newMessagesFrom.length > 0" class="justify-end">
      <v-col cols="6">
        <v-alert type="info" dismissible @input="newMessagesFrom = []">
          New messages from: {{ newMessagesFrom.join(", ") }}
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" align="center" justify="center">
        <v-row>
          <v-col md="12">
            <div class="d-flex align-center justify-center">
              <h2 align="center" justify="center">1-on-1 Weekly</h2>
              <div v-if="this.subscriptions && this.subscriptions.length > 0" class="ml-2 caption">
                <vue-json-to-csv :json-data="CSVData" csv-title="grad_clients">
                  <v-btn x-small>
                    Export
                  </v-btn>
                </vue-json-to-csv>
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="d-flex justify-end">
            <v-checkbox
              class="mr-3"
              v-model="hideResolved"
              @change="applyFilters(subscriptions)"
              label="Show unresolved chats"
            />
          </v-col>
        </v-row>
        <v-data-table
          height="1000"
          :headers="table.headers"
          :options="table.options"
          :items="subscriptions"
          :item-class="rowClass"
          :search="search"
          :show-group-by="true"
          :hide-default-footer="true"
          disable-pagination
          fixed-header
          dense
          :loading="$apollo.loading"
          :custom-sort="customSort"
        >
          <template v-slot:top>
            <v-text-field v-model="search" class="mx-4 mt-0 pt-0">
              <template v-slot:label>
                <v-icon color="secondary"> mdi-magnify </v-icon>
                Search
              </template>
            </v-text-field>
          </template>

          <template v-slot:[`item.profile_pic`]="props">
            <profile-pic :profilePic="props.item.profile_pic" :userId="props.item.userId" />
          </template>

          <template v-slot:[`header.client_name`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.client_name`]="props">
            <client-name
              :client-id="props.item.userId"
              :dob="props.item.dateOfBirth"
              :profileLink="props.item.profile_link"
              :subscriptionEndDate="props.item.subscriptionEndDate"
              :clientName="props.item.client_name"
              :username="props.item.username"
              :contactLink="props.item.contact_link"
              :billingDate="props.item.billingDate"
              :lastActive="props.item.client.userProfile.lastActive"
              :nextSubscription="props.item.nextSubscription"
              :coachingChat="props.item.client.userProfile.coachingChat"
              :supportChat="props.item.client.userProfile.supportChat"
              :coachId="coachId"
              :subscriptionSurveyId="props.item.subscriptionSurveyId"
            />
          </template>

          <template v-slot:[`item.controls`]="props" width="100">
            <tool-buttons
              :coachId="coachId"
              :userId="props.item.userId"
              :username="props.item.username"
              :clientName="props.item.client_name"
              :activityLoggedToday="props.item.activity_logged_today"
              :injectedData="props.item.user"
              class="text-center mb-2 mt-1"
            />
            <client-week-dots :prizeData="props.item.prizeEligibility" show-arrows :userId="props.item.userId" />
          </template>

          <template v-slot:[`header.activeWorkoutBlocks`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.activeWorkoutBlocks`]="props">
            <active-workout-blocks
              :workoutBlocks="props.item.activeWorkoutBlocks"
              :nextWorkoutBlocks="props.item.nextWorkoutBlocks"
            />
          </template>

          <template v-slot:[`header.nutritionPlan`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>

          <template v-slot:[`item.comms`]="props">
            <comms
              :item="props.item"
              activity-type="weekly"
              @update-am="updateCheckbox(props.item, 'Morning')"
              @update-pm="updateCheckbox(props.item, 'Afternoon')"
            />
          </template>

          <template v-slot:[`item.stickyNote`]="props">
            <sticky-note :note="props.item.stickyNote" />
          </template>

          <template v-slot:[`item.nutritionPlan`]="props">
            <span class="block caption"> <b>Plan:</b> {{ props.item.nutritionPlan.title }} </span>
            <span class="block caption"> <b>Block:</b> {{ props.item.nutritionBlock }} </span>
          </template>

          <template v-slot:[`header.lastMessage`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.lastMessage`]="props">
            <last-message-column @viewed-message="onMessageViewed" :message="props.item.lastMessage" />
          </template>
          <template v-slot:[`header.resolved`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.resolved`]="props">
            <resolve-message-btn :chat="props.item.chat" :resolved="props.item.resolved" @reload="reloadData" />
          </template>

          <template v-slot:[`header.lastCoachingAssessmentDate`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.lastCoachingAssessmentDate`]="props">
            <span
              v-if="props.item.lastCoachingAssessmentDate"
              :class="getAssessmentClass(props.item.lastCoachingAssessmentDate)"
              >{{ formatAssessmentDate(props.item.lastCoachingAssessmentDate) }}</span
            >
            <span v-else class="red--text">No check in!</span>
          </template>

          <template v-slot:[`header.lastUserAssessmentDate`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.lastUserAssessmentDate`]="props">
            <span
              v-if="props.item.lastUserAssessmentDate"
              :class="getAssessmentClass(props.item.lastUserAssessmentDate)"
              >{{ formatAssessmentDate(props.item.lastUserAssessmentDate) }}</span
            >
            <span v-else class="red--text">No check in!</span>
          </template>
          <template v-slot:[`header.weeksOnProgram`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>

          <template v-slot:[`header.has_goal`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.has_goal`]="props">
            <bhag :hasGoal="props.item.has_goal" />
          </template>
          <template v-slot:[`header.has_checkin_goal`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.has_checkin_goal`]="props">
            <checkin-goal :hasGoal="props.item.has_checkin_goal" :agedGoal="props.item.has_aged_checkin_goal" />
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import MetricMixin from "@/components/shared/mixins/MetricMixin";
import CoachingDashboardBase from "../mixins/CoachingDashboardBase";
import OneOnOneDashboardBase from "../mixins/OneOnOneDashboardBase";

export default {
  name: "CoachingGradSubscription",
  props: {
    coachId: {
      type: String,
      required: false,
    },
  },
  mixins: [CoachingDashboardBase, MetricMixin, OneOnOneDashboardBase],
  data() {
    return {
      encodedCoachId: btoa("UserNode:" + this.coachId),
      productProgramType: "1-on-1 Weekly",
      search: "",
      defaultCompleteSelectionText: "Hide rows",
      selectedUsername: "",
      selectedFullname: "",
      queryProgramType: "",
      subscriptions: [],
      table: {
        options: {
          showGroupBy: true,
          multiSort: false,
        },
        headers: [
          { text: "", value: "profile_pic", groupable: false },
          { text: "Client", value: "client_name", groupable: false },
          {
            text: "Comms",
            value: "comms",
            align: "center",
            sortable: true,
            groupable: false,
            help: "Client communications AM / PM ",
          },
          { text: "", value: "stickyNote", groupable: false },
        ],
      },
    };
  },
  methods: {
    customSort(items, index, isDesc) {
      items.sort((a, b) => {
        if (!isNaN(a[index[0]])) {
          if (!isDesc[0]) {
            return a[index[0]] - b[index[0]];
          } else {
            return b[index[0]] - a[index[0]];
          }
        } else {
          if (!isDesc[0]) {
            return a[index[0]] < b[index[0]] ? -1 : 1;
          } else {
            return b[index[0]] < a[index[0]] ? -1 : 1;
          }
        }
      });
      return items;
    },
  },
};
</script>

<style scoped>
.max-width {
  width: 100%;
}

.exercise-dot {
  position: absolute;
  top: 16px;
  background-color: var(--v-exercise-base);
  border: 1px solid white;
  border-radius: 100%;
  width: 22px;
  height: 22px;
}
.block {
  display: block;
}
</style>
